import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CalendarDays, ChevronLeft, ChevronRight, Loader2, MapPin, Check, X,
  Play, Square, Timer, User, Package, RotateCcw,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { getMyPlanning, getPlanningWeek, respondToEvent, finishEvent, resumeEvent } from "../services/api";
import { MyAbsencesTab } from "../components/employees/MyAbsencesTab";
import { WeekGrid } from "../components/employees/WeekGrid";
import { EventDialog } from "../components/employees/EventDialog";
import { PlanningNavigation } from "../components/employees/PlanningNavigation";
import { MobileEventButton } from "../components/employees/MobileEventButton";
import { useAuth } from "../context/AuthContext";
import { typeIcon, STATUS_META, PRIORITY_META } from "../utils/eventVisuals";

const iso = (d) => d.toLocaleDateString("fr-CA");
const mondayOf = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const fmtDay = (dIso) => new Date(dIso + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
const fmtDT = (isoStr) => new Date(isoStr).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

const fmtDuration = (mins) => {
  const h = Math.floor(mins / 60), m = mins % 60;
  return h ? `${h}h${String(m).padStart(2, "0")}` : `${m} min`;
};

const Chrono = ({ since, base }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const secs = (base || 0) + Math.max(0, Math.floor((Date.now() - new Date(since).getTime()) / 1000));
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
  return (
    <span className="font-mono text-base font-bold text-[#26788A] tabular-nums" data-testid="task-chrono">
      {String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
    </span>
  );
};

const MyEventCard = ({ ev, onRespond, onFinish, onResume, onOpen, busy }) => {
  const Icon = typeIcon(ev.event_type);
  const st = STATUS_META[ev.status] || STATUS_META.todo;
  const prio = PRIORITY_META[ev.priority] || PRIORITY_META.normal;
  const a = ev.my_assignment || { status: "pending" };
  const cancelled = ev.status === "cancelled";
  return (
    <div className={`bg-white border border-slate-200 rounded-xl p-4 ${cancelled ? "opacity-60" : ""}`} data-testid={`my-event-${ev.id}`}>
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: `${ev.color}22` }}>
          <Icon size={16} style={{ color: ev.color }} />
        </span>
        <div className="flex-1 min-w-0">
          <button onClick={() => onOpen(ev)} className={`text-left min-h-11 text-sm font-semibold text-slate-900 break-words [overflow-wrap:anywhere] ${cancelled ? "line-through" : ""}`} data-testid={`my-event-open-${ev.id}`}>{ev.title}</button>
          <p className="text-xs text-slate-400 mt-0.5 flex flex-wrap items-center gap-x-2">
            <span>{ev.event_type}</span>
            {ev.start_time && <span>· {ev.start_time}{ev.end_time ? `–${ev.end_time}` : ""}</span>}
            {ev.location && <span className="flex items-center gap-0.5">· <MapPin size={10} /> {ev.location}</span>}
          </p>
          {ev.description && <p className="text-xs text-slate-500 mt-1">{ev.description}</p>}
          {(ev.equipment_items || []).length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`my-event-equipment-${ev.id}`}>
              {ev.equipment_items.map((x) => (
                <span key={x.equipment_id} className="flex items-center gap-1 text-[10px] font-medium text-[#26788A] bg-[#26788A]/10 px-2 py-0.5 rounded-full">
                  <Package size={9} /> {x.name}{x.quantity > 1 ? ` × ${x.quantity}` : ""}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className="flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ color: st.color, backgroundColor: `${st.color}1A` }}>
            <st.icon size={10} /> {st.label}
          </span>
          <span className="flex items-center gap-1 text-[10px] font-medium" style={{ color: prio.color }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: prio.color }}></span>{prio.label}
          </span>
        </div>
      </div>

      {!cancelled && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          {a.status === "pending" && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full flex-1 min-w-[160px]">
                Cette mission vous a été attribuée{a.assigned_by ? ` par ${a.assigned_by}` : ""} — cliquez sur Commencer quand vous démarrez
              </span>
              <Button size="sm" className="h-8 bg-amber-500 hover:bg-amber-600 text-white" disabled={busy} onClick={() => onRespond(ev, "accept")} data-testid={`start-event-${ev.id}`}>
                <Play size={13} className="mr-1" /> Commencer
              </Button>
            </div>
          )}
          {a.status === "accepted" && (
            <div className="flex flex-wrap items-center gap-3">
              <span className="flex items-center gap-1.5 text-xs text-slate-500">
                <Play size={12} className="text-[#26788A]" /> Commencée le {fmtDT(a.accepted_at)}
              </span>
              <span className="flex items-center gap-1.5 bg-teal-50 px-3 py-1 rounded-full">
                <Timer size={13} className="text-[#26788A]" />
                <Chrono since={a.accepted_at} base={a.time_spent_seconds || 0} />
              </span>
              <Button size="sm" className="h-8 bg-[#26788A] hover:bg-[#1D5E6C] text-white ml-auto" disabled={busy} onClick={() => onFinish(ev)} data-testid={`finish-event-${ev.id}`}>
                <Square size={12} className="mr-1.5" /> Terminer
              </Button>
            </div>
          )}
          {a.status === "done" && (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs font-medium text-emerald-600 flex items-center gap-1.5 flex-1 min-w-[180px]">
                <Check size={13} /> Terminée en {fmtDuration(a.duration_minutes || 0)}
                {a.finished_at && <span className="text-slate-400 font-normal">· le {fmtDT(a.finished_at)}</span>}
              </p>
              <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => onResume(ev)} data-testid={`resume-event-${ev.id}`}>
                <RotateCcw size={12} className="mr-1.5" /> Reprendre
              </Button>
            </div>
          )}
          {a.status === "refused" && (
            <p className="text-xs font-medium text-red-500 flex items-center gap-1.5"><X size={13} /> Vous avez refusé cette mission</p>
          )}
        </div>
      )}
    </div>
  );
};

export default function MyPlanningPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user?.role === "admin" || !!user?.can_manage_events;
  const [tab, setTab] = useState("mine");
  const [mine, setMine] = useState(null);
  const [week, setWeek] = useState(null);
  const [refDate, setRefDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [eventDialog, setEventDialog] = useState(null);
  const autoSwitched = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const today = new Date();
      const [weekRes, mineRes] = await Promise.all([
        getPlanningWeek(iso(mondayOf(refDate))),
        getMyPlanning(iso(today), iso(addDays(today, 13))),
      ]);
      setWeek(weekRes.data);
      setMine(mineRes.data);
      if (!mineRes.data.employee && !autoSwitched.current) {
        autoSwitched.current = true;
        setTab("full");
      }
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [refDate]);

  useEffect(() => { load(); }, [load]);

  const handleRespond = async (ev, action) => {
    try {
      setBusy(true);
      await respondToEvent(ev.id, action);
      toast.success("Mission commencée — le chronomètre est lancé");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const handleResume = async (ev) => {
    try {
      setBusy(true);
      await resumeEvent(ev.id);
      toast.success("Mission reprise — le chronomètre est relancé");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const handleFinish = async (ev) => {
    try {
      setBusy(true);
      await finishEvent(ev.id);
      toast.success("Mission terminée — bravo !");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const eventsByDay = (mine?.events || []).reduce((acc, ev) => {
    (acc[ev.date] = acc[ev.date] || []).push(ev);
    return acc;
  }, {});

  const pendingCount = (mine?.events || []).filter((e) => (e.my_assignment?.status || "pending") === "pending" && e.status !== "cancelled").length;

  const myEmpId = mine?.employee?.id;
  const myGridEmployees = (week?.employees || []).filter((e) => e.id === myEmpId);
  const myGridEvents = (week?.events || []).filter((ev) => (ev.employee_ids || []).length === 0 || ev.employee_ids.includes(myEmpId));

  const weekNav = (
    <PlanningNavigation selectedDate={refDate} onDateChange={setRefDate} title={`Semaine du ${week ? new Date(week.week_start + "T00:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : ""}`} onPrevious={() => setRefDate(addDays(refDate, -7))} onNext={() => setRefDate(addDays(refDate, 7))} onToday={() => setRefDate(new Date())} previousId="full-prev" nextId="full-next" titleId="full-week-title" todayId="full-today" />
  );

  return (
    <div className="planning-page p-4 lg:p-8 max-w-7xl mx-auto min-w-0 pb-24" data-testid="my-planning-page">
      <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 mb-4">
        <CalendarDays size={24} className="text-[#26788A]" /> Planning
      </h1>

      <div className="grid grid-cols-3 sm:flex gap-1 bg-slate-100 p-1 rounded-lg mb-5 sm:w-fit">
        <button className={`min-w-0 min-h-11 px-2 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium ${tab === "full" ? "bg-white text-[#26788A] shadow-sm" : "text-slate-600"}`} onClick={() => setTab("full")} data-testid="tab-full-planning">
          Planning complet
        </button>
        <button className={`relative min-w-0 min-h-11 px-2 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium ${tab === "mine" ? "bg-white text-[#26788A] shadow-sm" : "text-slate-600"}`} onClick={() => setTab("mine")} data-testid="tab-my-planning">
          Mon planning
          {pendingCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{pendingCount}</span>
          )}
        </button>
        <button className={`min-w-0 min-h-11 px-2 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium ${tab === "absences" ? "bg-white text-[#26788A] shadow-sm" : "text-slate-600"}`} onClick={() => setTab("absences")} data-testid="tab-my-absences">
          Absences
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={26} className="animate-spin text-slate-300" /></div>
      ) : tab === "absences" ? (
        <MyAbsencesTab employeeId={mine?.employee?.id} />
      ) : tab === "mine" ? (
        !mine?.employee ? (
          <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-xl">
            <User size={30} className="mx-auto text-slate-300 mb-2" />
            <p className="text-slate-500 text-sm">Aucune fiche employé n'est liée à votre compte.</p>
            <p className="text-slate-400 text-xs mt-1">Demandez à un administrateur de lier votre compte dans l'onglet Utilisateurs.</p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="my-planning-content">
            {/* Ma semaine — même tableau que le planning employés, filtré sur moi */}
            <div>
              {weekNav}
              {myGridEmployees.length > 0 ? (
                <WeekGrid
                  focusDate={iso(refDate)}
                  days={week?.days || []}
                  employees={myGridEmployees}
                  allEmployees={week?.employees || []}
                  events={myGridEvents}
                  tasks={(week?.tasks || []).filter((t) => t.assign_all || (t.employee_ids || []).includes(myEmpId))}
                  canManage={canManage}
                  onEventClick={(ev) => setEventDialog({ event: ev })}
                  onTaskClick={() => navigate("/taches")}
                  onChanged={load}
                />
              ) : (
                <div className="text-center py-10 bg-white border border-dashed border-slate-200 rounded-xl">
                  <p className="text-slate-400 text-sm">Votre fiche employé n'apparaît pas dans le planning de cette semaine.</p>
                </div>
              )}
            </div>

            {/* Mes missions */}
            <div>
              <p className="text-[11px] uppercase tracking-[0.15em] font-semibold text-slate-400 mb-2">Mes missions (14 jours)</p>
              {Object.keys(eventsByDay).length === 0 ? (
                <div className="text-center py-10 bg-white border border-dashed border-slate-200 rounded-xl">
                  <p className="text-slate-400 text-sm">Aucune mission attribuée sur les 14 prochains jours.</p>
                </div>
              ) : (
                <div className="space-y-4 max-w-3xl">
                  {Object.entries(eventsByDay).map(([day, evs]) => (
                    <div key={day}>
                      <p className="text-xs font-semibold text-slate-500 capitalize mb-1.5">{fmtDay(day)}</p>
                      <div className="space-y-2">
                        {evs.map((ev) => (
                          <MyEventCard key={ev.id} ev={ev} onRespond={handleRespond} onFinish={handleFinish} onResume={handleResume} onOpen={ev => setEventDialog({ event: ev })} busy={busy} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )
      ) : (
        <div data-testid="full-planning-content">
          {weekNav}
          {!week?.employees?.length ? (
            <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-xl"><p className="text-slate-500">Aucun employé actif.</p></div>
          ) : (
            <WeekGrid
              focusDate={iso(refDate)}
              days={week?.days || []}
              employees={week?.employees || []}
              events={week?.events || []}
              tasks={week?.tasks || []}
              canManage={canManage}
              onEventClick={(ev) => setEventDialog({ event: ev })}
              onTaskClick={() => navigate("/taches")}
              onChanged={load}
            />
          )}
        </div>
      )}

      {canManage && tab !== "absences" && <MobileEventButton onClick={() => setEventDialog({ defaultDate: new Date().toLocaleDateString("fr-CA") })} />}
      <EventDialog
        open={!!eventDialog}
        onClose={() => setEventDialog(null)}
        event={eventDialog?.event}
        defaultDate={eventDialog?.defaultDate}
        onSaved={load}
      />
    </div>
  );
}
